import { Grid, Stack, Typography } from '@mui/material';
import { Button } from '@uy3/web-components';

import { useState } from 'react';
import { first, isEmpty } from 'lodash';
import {
    ApiResponseError,
    handleOnError,
    showSuccessToast,
    toastState,
    ToastType
} from 'contexts/apiRequestContext';
import { Modal } from '@uy3/web-components';
import Toast from 'components/Toast/Toast';
import { useFormContext } from 'contexts/formContext';
import { DataPrevMarginQueryList } from './DataPrevMarginQueryList';
import { useParams } from 'react-router';
import { useDataprevMarginQuery, useUpdateDataprevMarginQuery } from 'contexts/dataprev';
import { RefreshProgress } from 'components/RefreshProgress';
import { ActionButtons } from 'components/ActionButtons';

export const DataPrevMarginQuery = () => {
    let { personId } = useParams();
    const [toast, setToast] = useState<ToastType>(toastState);
    const { watch } = useFormContext();
    const [registrationNumber] = watch(['registrationNumber']);
    const effectivePersonId = personId || watch('personId');
    const effectiveRegistrationNumber =
        watch('beneficiaryRegistrationNumber') || registrationNumber;

    const creditProductId = watch('productId');

    const successMessage = 'Consulta de margem realizada com sucesso!';
    const onSuccessDataprev = () => {
        dataprevRefetch();
        showSuccessToast(successMessage, undefined, setToast);
    };

    const onError = (error: ApiResponseError) => handleOnError(error, setToast);

    const {
        data: dataprevResponseData,
        refetch: dataprevRefetch,
        isLoading: isLoadingDataPrev,
        isFetching: isFetchingDataPrev
    } = useDataprevMarginQuery(
        {
            registrationNumber: effectiveRegistrationNumber,
            personId: effectivePersonId,
            creditProductId
        },
        true,
        setToast
    );

    const { mutateAsync: dataprevMarginMutation, isLoading: isLoadingUpdateDataprev } =
        useUpdateDataprevMarginQuery(onSuccessDataprev, onError);

    const handleDataPrevMarginMutation = async () => {
        await dataprevMarginMutation({
            registrationNumber: effectiveRegistrationNumber,
            personId: effectivePersonId
        });
    };

    const hasData = !isEmpty(dataprevResponseData);
    const dataPrevData = first(dataprevResponseData);
    const lastUpdateQueryData = dataPrevData?.updatedAt;
    const queryDataToList = dataPrevData?.result ?? [];
    const isLoading = isLoadingDataPrev || isFetchingDataPrev || isLoadingUpdateDataprev;

    const [openModal, setOpenModal] = useState<boolean>(false);

    const handleOpenModal = () => setOpenModal(true);
    const closeModal = () => setOpenModal(false);

    return (
        <Stack spacing={2} mt={-4}>
            <Toast toast={toast} setToast={setToast} />
            <RefreshProgress refreshing={isLoading} />
            <Modal
                title="Valores originais"
                open={openModal}
                onClose={closeModal}
                children={
                    <pre style={contentStyle}>{JSON.stringify(dataprevResponseData, null, 2)}</pre>
                }
                sizeModal="large"
            />
            {!hasData ? (
                <Grid item xs={12}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={4}
                        mt={2}
                        p={5}
                        sx={{
                            backgroundColor: '#F4F3F3',
                            borderRadius: 2,
                            height: '104px'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: 'Lato, sans-serif',
                                fontWeight: 400,
                                fontSize: '19px',
                                color: '#292927'
                            }}
                        >
                            A margem ainda não foi consultada. Clique em 'Consultar margem' para
                            iniciar a verificação.
                        </Typography>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleDataPrevMarginMutation}
                            disabled={isLoading}
                        >
                            Consultar margem
                        </Button>
                    </Stack>
                </Grid>
            ) : (
                <Stack>
                    {queryDataToList?.length > 0 && (
                        <DataPrevMarginQueryList
                            queryData={queryDataToList}
                            isLoading={isLoading}
                            updateQueryData={handleDataPrevMarginMutation}
                        />
                    )}
                    <ActionButtons
                        lastUpdate={lastUpdateQueryData}
                        buttons={[
                            {
                                label: 'Ver dados originais',
                                variant: 'outlined',
                                onClick: handleOpenModal,
                                disabled: isLoading
                            },
                            {
                                label: 'Consultar novamente',
                                variant: 'contained',
                                onClick: handleDataPrevMarginMutation,
                                disabled: isLoading
                            }
                        ]}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export const contentStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    maxWidth: '100%'
};
